/**
 * External dependencies
 */
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Text } from '@towersystems/towersystems-design-system';
import { map, get } from 'lodash';

/**
 * Internal dependencies
 */
import Modal from 'components/modal';
import { createCloseModalAction } from 'state/ui/actions';
import { isModalVisible } from 'state/ui/selectors';
import Logs from './logs';
import { requestOperation } from 'state/operations/actions';
import { getOperationId } from 'state/ui/operation-details-modal/selectors';
import Icon from 'components/icon';
import Tag from 'components/tag';
import Skeleton from 'react-loading-skeleton';
import Status from './status';
import Card from 'components/card';
import DateTime from 'components/date-time';

/**
 * Style dependencies
 */
import './styles.scss';

const OperationDetailsModal = ({ operationId, isVisible, onClosed, close, ...props }) => {
    const [operation, setOperation] = useState(null);
    const [requesting, setRequesting] = useState(true);

    useEffect(() => {
        let time1;
        // create timeout to keep refreshing the data
        if (isVisible && operationId) {
            loadData();
            time1 = setInterval(() => loadData(true), 30000);
        } else {
            clearInterval(time1);
        }

        return () => clearInterval(time1);
    }, [operationId, isVisible]);

    const loadData = async (scroll) => {
        try {
            setRequesting(true);
            let result = await props.requestOperation(operationId);
            setOperation(result);

            scroll && operation && operation.status == 'processing' && scrollToBottom();
        } catch (error) {}
        setRequesting(false);
    };

    const handleOnClosed = () => {
        setOperation(null);
        setRequesting(false);
        onClosed && onClosed();
    };

    const logsEndRef = useRef(null);

    const scrollToBottom = () => {
        logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <Modal
            className='operation-details-modal'
            close={close}
            isVisible={isVisible}
            title='Sync log'
            onClosed={handleOnClosed}
        >
            <Box padding='large'>
                <Stack space='xlarge'>
                    <Box className='operation-details__logs-header' display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
                        {requesting && !operation ? (
                            <Box flexDirection='column' display='flex' alignItems='center' justifyContent='center'>
                                <Skeleton height={10} width={300}  />
                                <Box marginTop='large' style={{ height: 50, width: 300 }}>
                                    <Skeleton />
                                </Box>
                            </Box>
                        ) : (
                            <Stack space='large'>
                                <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
                                    <Box>
                                        <Text size='large' marginTop='large'>
                                            Sync is&nbsp;
                                            <Tag className={get(operation, 'status', 'unknown')}> {get(operation, 'status', 'unknown')}</Tag>
                                        </Text>
                                    </Box>
                                </Box>
                                {get(operation, 'status') == 'complete' && (
                                    <Box>
                                        <Text align='center' size='small'>
                                            Completed at {<DateTime date={get(operation, 'completed_at')} format='Do MMM, YYYY HH:mm:ss' />}
                                        </Text>
                                    </Box>
                                )}
                                {get(operation, 'error') && (
                                    <Box>
                                        <Text align='center' size='small'>
                                            Error: {get(operation, 'error')}
                                        </Text>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Box>
                    <Card>
                        <Box className='operation-details__logs-wrapper'>
                            {!(requesting && !operation) && <Logs operation={operation} requesting={requesting} />}
                            <div ref={logsEndRef} />
                        </Box>
                    </Card>
                </Stack>
            </Box>
        </Modal>
    );
};

export default connect(
    (state) => ({
        isVisible: isModalVisible('OperationDetailsModal')(state),
        operationId: getOperationId(state),
    }),
    {
        close: createCloseModalAction('OperationDetailsModal'),
        requestOperation,
    },
)(OperationDetailsModal);
